import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../../services/api";
import {
  Button,
  ListGroup,
  Breadcrumb,
  Alert,
  Modal,
  Form,
  Pagination,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  FaFolder,
  FaFolderOpen,
  FaVideo,
  FaFilePdf,
  FaFileAlt,
  FaPlus,
  FaUpload,
  FaTrash,
  FaDownload,
  FaEye,
  FaHome,
  FaYoutube,
  FaPlay,
  FaSearch,
  FaEdit,
  FaCopy,
  FaFileArchive,
} from "react-icons/fa";
import { toast } from "react-toastify";
import BatchMaterialService from "../../../../services/batchMaterialService";
import BatchService from "../../../../services/batchService";
import UploadMaterialModal from "./UploadMaterialModal";
import AdoptMaterialsModal from "./AdoptMaterialsModal";

const BatchMaterialsTab = ({ batchId }) => {
  const [materials, setMaterials] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAdoptModal, setShowAdoptModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [currentYoutubeVideo, setCurrentYoutubeVideo] = useState(null);
  const [folderCounts, setFolderCounts] = useState({});

  // Pagination states
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    loadMaterials();
  }, [currentFolder, pagination.current_page, itemsPerPage, searchTerm]);

  useEffect(() => {
    if (currentFolder) {
      loadFolderPath();
    } else {
      setFolderPath([]);
    }
  }, [currentFolder]);

  const loadFolderCount = async (folderId) => {
    try {
      const response = await BatchMaterialService.getFolderItemCount(
        batchId,
        folderId,
      );
      return response?.data?.count || 0;
    } catch (error) {
      console.error("Failed to load folder count:", error);
      return 0;
    }
  };

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const response = await BatchMaterialService.getMaterials(
        batchId,
        currentFolder,
        pagination.current_page,
        itemsPerPage,
        searchTerm,
      );

      if (response.data && response.data.data) {
        setMaterials(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        setMaterials([]);
      }

      // Load folder counts
      const folders = materials.filter((item) => item.type === "folder");
      const counts = {};
      for (const folder of folders) {
        counts[folder.id] = await loadFolderCount(folder.id);
      }
      setFolderCounts(counts);
    } catch (error) {
      toast.error("Failed to load materials");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderPath = async () => {
    if (!currentFolder) return;
    try {
      const response = await BatchMaterialService.getFolderPath(
        batchId,
        currentFolder,
      );
      setFolderPath(response.data || []);
    } catch (error) {
      console.error("Failed to load folder path:", error);
    }
  };

  const handleFolderClick = (folder) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setCurrentFolder(folder.id);
  };

  const handleBreadcrumbClick = (index) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    if (index === -1) {
      setCurrentFolder(null);
    } else {
      setCurrentFolder(folderPath[index].id);
    }
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // Search handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;

    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{" "}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)}{" "}
            of {pagination.total} items
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Folder name is required");
      return;
    }

    try {
      await BatchMaterialService.createFolder(batchId, {
        name: newFolderName,
        parent_id: currentFolder,
      });
      toast.success("Folder created successfully");
      setShowFolderModal(false);
      setNewFolderName("");
      loadMaterials();
    } catch (error) {
      toast.error("Failed to create folder");
    }
  };

  const handleEditClick = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedItem(item);
    setEditName(item.name);
    setEditUrl(item.url_link || "");
    setShowEditModal(true);
  };

  const handleUpdateMaterial = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }

    const updateData = { name: editName };
    if (selectedItem?.type === "link" && editUrl) {
      if (!editUrl.match(/^https?:\/\//)) {
        toast.error("Please enter a valid URL");
        return;
      }
      updateData.url_link = editUrl;
    }

    try {
      await BatchMaterialService.updateMaterial(
        batchId,
        selectedItem.id,
        updateData,
      );
      toast.success("Updated successfully");
      setShowEditModal(false);
      setSelectedItem(null);
      loadMaterials();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      await BatchMaterialService.deleteMaterial(batchId, selectedItem.id);
      toast.success("Deleted successfully");
      setShowDeleteModal(false);
      setSelectedItem(null);
      loadMaterials();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleViewFile = (file) => {
    const fileUrl = `${API_BASE_URL}/${file.file_path}`;
    window.open(fileUrl, "_blank");
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  const playYoutubeVideo = (item) => {
    setCurrentYoutubeVideo(item);
    setShowYoutubeModal(true);
  };

  const getIcon = (type) => {
    switch (type) {
      case "folder":
        return <FaFolder className="text-warning me-2" size={20} />;
      case "video":
        return <FaVideo className="text-primary me-2" size={20} />;
      case "pdf":
        return <FaFilePdf className="text-danger me-2" size={20} />;
      case "link":
        return <FaYoutube className="text-danger me-2" size={20} />;
      case "zip":
        return <FaFileArchive className="text-success me-2" size={20} />;
      default:
        return <FaFileAlt className="text-secondary me-2" size={20} />;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Batch Materials</h5>
        <div>
          <Button
            variant="info"
            size="sm"
            className="me-2"
            onClick={() => setShowAdoptModal(true)}
          >
            <FaCopy className="me-1" /> Adopt Materials
          </Button>
          <Button
            variant="warning"
            size="sm"
            className="me-2"
            onClick={() => setShowFolderModal(true)}
          >
            <FaPlus className="me-1" /> New Folder
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload className="me-1" /> Upload
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <InputGroup className="mb-3" style={{ width: "300px" }}>
        <Form.Control
          placeholder="Search by title..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleSearchKeyPress}
        />
        <Button variant="outline-primary" onClick={handleSearch}>
          <FaSearch />
        </Button>
        {searchTerm && (
          <Button variant="outline-secondary" onClick={handleClearSearch}>
            Clear
          </Button>
        )}
      </InputGroup>

      {/* Breadcrumb */}
      <Breadcrumb className="mb-4 p-3 bg-light rounded">
        <Breadcrumb.Item
          onClick={() => handleBreadcrumbClick(-1)}
          active={!currentFolder}
          style={{ cursor: "pointer" }}
        >
          <FaHome className="me-1" />
          Root
        </Breadcrumb.Item>
        {folderPath.map((folder, index) => (
          <Breadcrumb.Item
            key={folder.id}
            onClick={() => handleBreadcrumbClick(index)}
            active={index === folderPath.length - 1}
            style={{ cursor: "pointer" }}
          >
            {folder.name}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      {/* Materials List */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {materials.length === 0 ? (
            <Alert variant="info">
              <FaFolder className="me-2" />
              {searchTerm
                ? "No materials found matching your search."
                : "No materials in this folder. Create a folder or upload files."}
            </Alert>
          ) : (
            <>
              <ListGroup>
                {materials.map((item) => {
                  const thumbnail =
                    item.type === "link"
                      ? getYoutubeThumbnail(item.url_link)
                      : null;
                  return (
                    <ListGroup.Item
                      key={item.id}
                      className="d-flex justify-content-between align-items-center py-3"
                      style={{
                        cursor: item.type === "folder" ? "pointer" : "default",
                      }}
                      onClick={() =>
                        item.type === "folder" && handleFolderClick(item)
                      }
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {item.type === "link" && thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={item.name}
                            style={{
                              width: "50px",
                              height: "35px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              marginRight: "12px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          getIcon(item.type)
                        )}
                        <div className="d-flex align-items-center flex-wrap">
                          <strong className="me-2">{item.name}</strong>
                          {item.type === "folder" &&
                            folderCounts[item.id] !== undefined && (
                              <Badge bg="secondary" className="ms-1">
                                {folderCounts[item.id]}{" "}
                                {folderCounts[item.id] === 1 ? "item" : "items"}
                              </Badge>
                            )}
                          {item.type === "zip" && (
                            <Badge bg="success" className="ms-1">
                              📦 ZIP
                            </Badge>
                          )}
                          {item.type !== "folder" && item.type !== "link" && item.type !== "zip" && (
                            <small className="text-muted ms-2">
                              {item.file_size
                                ? `${(item.file_size / 1024).toFixed(2)} KB`
                                : ""}
                            </small>
                          )}
                        </div>
                      </div>

                      <div>
                        {item.type !== "folder" &&
                          (item.type === "link" ? (
                            <Button
                              variant="danger"
                              size="sm"
                              className="me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                playYoutubeVideo(item);
                              }}
                            >
                              <FaPlay /> Watch
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewFile(item);
                                }}
                              >
                                <FaEye /> View
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                href={`${window.location.origin}/backend/${item.file_path}`}
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FaDownload /> Download
                              </Button>
                            </>
                          ))}
                        {/* Up and Down buttons for reordering */}
                        <Button
                          variant="secondary"
                          size="sm"
                          className="me-2"
                          title="Move Up"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await BatchMaterialService.moveUp(
                                batchId,
                                item.id,
                              );
                              toast.success("Material moved up");
                              loadMaterials();
                            } catch (error) {
                              toast.error("Failed to move material");
                            }
                          }}
                          disabled={materials.indexOf(item) === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="me-2"
                          title="Move Down"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await BatchMaterialService.moveDown(
                                batchId,
                                item.id,
                              );
                              toast.success("Material moved down");
                              loadMaterials();
                            } catch (error) {
                              toast.error("Failed to move material");
                            }
                          }}
                          disabled={
                            materials.indexOf(item) === materials.length - 1
                          }
                        >
                          ↓
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={(e) => handleEditClick(item, e)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              {renderPagination()}
            </>
          )}
        </>
      )}

      {/* Create Folder Modal */}
      <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Folder Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFolderModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateFolder}>
            Create Folder
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Modal */}
      <UploadMaterialModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        batchId={batchId}
        currentFolder={currentFolder}
        onUpload={loadMaterials}
      />

      {/* Adopt Materials Modal */}
      <AdoptMaterialsModal
        show={showAdoptModal}
        onHide={() => setShowAdoptModal(false)}
        currentBatchId={batchId}
        onAdopt={loadMaterials}
      />

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2 text-warning" />
            Edit {selectedItem?.type === "folder" ? "Folder" : "Material"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter name"
              />
            </Form.Group>
            {selectedItem?.type === "link" && (
              <Form.Group className="mb-3">
                <Form.Label>URL</Form.Label>
                <Form.Control
                  type="url"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateMaterial}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* YouTube Player Modal */}
      <Modal
        show={showYoutubeModal}
        onHide={() => setShowYoutubeModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaYoutube className="text-danger me-2" />
            {currentYoutubeVideo?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {currentYoutubeVideo && (
            <div
              style={{
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(currentYoutubeVideo.url_link)}?autoplay=1&rel=0`}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={currentYoutubeVideo.name}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowYoutubeModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedItem.name}</strong>?
              {selectedItem.type === "folder" && (
                <Alert variant="warning" className="mt-2">
                  This folder and all its contents (
                  {folderCounts[selectedItem.id] || 0} items) will be deleted.
                </Alert>
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BatchMaterialsTab;